import { useMemo } from 'react';
import { Icon } from '../../components/Icon';
import { localDateISO, locationLabel, whereIsAt } from '../../domain/places';
import type { PersonWithNames } from '../../domain/types';
import { useI18n } from '../../i18n';
import { useApp } from '../../app/AppState';
import { Link } from '../../app/router';
import { formatDay } from './VisitsEditor';

/** Today's date, recomputed on each render (cheap) — the same for everyone on the screen. */
export function useToday(): string {
  return useMemo(() => localDateISO(), []);
}

/**
 * Where the person is, as briefly as it can be said: a pin and the city.
 *
 * No "Lives in" in front of it — the pin already says that — and no clock: the
 * time somewhere else is interesting once and clutter every time after.
 *
 * Where they live comes first and stays first. A trip is a temporary thing that
 * happens to a person who lives somewhere, so the suitcase line sits *under*
 * the home line rather than replacing it — the answer to "where are they from"
 * never moves, and the answer to "where are they this week" is right below it.
 * Your own circle without a home gets a nudge instead.
 */
export function WhereLine({ person, nudge = false, className = '' }: { person: PersonWithNames; nudge?: boolean; className?: string }) {
  const { t, lang } = useI18n();
  const { places, locationsById } = useApp();
  const today = useToday();
  const now = whereIsAt(person, places, today);
  const home = person.homeLocationId ? locationsById.get(person.homeLocationId) : undefined;

  if (!now && !home) {
    if (!nudge) return null;
    return (
      <Link to={`/people/${person.id}/edit#where`} className={`card-nudge ${className}`}>
        <Icon name="pin" />
        <span>{t('places.nudge')}</span>
      </Link>
    );
  }
  const visiting = now?.kind === 'visit' && now.place ? now : null;
  // Their home, or — for someone with no home recorded — wherever they are.
  const homeLabel = locationLabel(home, lang) || (visiting ? '' : locationLabel(locationsById.get(now?.locationId ?? ''), lang));
  const visitLoc = visiting ? locationsById.get(visiting.locationId) : undefined;

  return (
    <div className={`where-line ${className}`}>
      {homeLabel ? (
        <span className="where-home">
          <Icon name="pin" />
          <span>{homeLabel}</span>
        </span>
      ) : null}
      {visiting?.place ? (
        <span className="where-visit">
          <Icon name="suitcase" />
          <span>{t('world.visitingUntil', { city: locationLabel(visitLoc, lang), date: formatDay(visiting.place.to ?? visiting.place.from, lang) })}</span>
        </span>
      ) : null}
    </div>
  );
}
